import {
  CDMC_Object,
  CDMC_Dependency,
  CDMC_Event,
  CDMC_Transition,
  CDMC_FSM,
  CDMC_State,
  CDMC_Inheritance,
} from '../metamodel';
import {
  removeEDGNode,
  removeOETColumn,
  removeEDGLink,
  removeOETRow,
  removeFSMView,
  removeFSMNode,
  removeFSMLink,
  removeEDGInheritanceLink,
} from '../uimodel';
import {
  ensureDependency,
  ensureObject,
  ensureEvent,
  ensureFSM,
  ensureTransition,
  ensureState,
  ensureInheritance,
} from '../metamodel/util';
import type { IModel, IdOptionMap, OrId } from '../types';
import type { IObjectType } from '../../../stores/model/metamodel/object_type';
import type { IEventType } from '../../../stores/model/metamodel/event_type';
import type { IDependency } from '../../../stores/model/metamodel/dependency';
import type { IInheritance } from '../../../stores/model/metamodel/inheritance';
import type { IFSM } from '../../../stores/model/metamodel/fsm';
import type { IState } from '../../../stores/model/metamodel/state';
import type { ITransition } from '../../../stores/model/metamodel/transition';

/**
 * Delete an object type and its EDG node and OET column.
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId The object type.
 * @param {boolean} removeEvents Should related events also be deleted?
 * @param {Map} inheritanceOptions Options to pass down when removing inheritances. Key is the id of
 *  the inheritance relation. See CDMC_Inheritance.
 */
export function Combined_DeleteObject(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  removeEvents: boolean,
  inheritanceOptions: Record<string, IdOptionMap | undefined>
) {
  const object = ensureObject(model.metamodel, objectOrId)!;
  removeEDGNode(model.uimodel, object);
  removeOETColumn(model.uimodel, object);
  return CDMC_Object(model, object.id, removeEvents, inheritanceOptions);
}

/**
 * Delete a dependency and its EDG link.
 *
 * @param {Model} model The model to operate on.
 * @param {Dependency or String} depOrId The dependency.
 */
export function Combined_DeleteDependency(model: IModel, depOrId: OrId<IDependency>) {
  const dependency = ensureDependency(model.metamodel, depOrId)!;
  removeEDGLink(model.uimodel, dependency);
  return CDMC_Dependency(model, dependency);
}

/**
 * Delete an inheritance relation and its EDG link.
 *
 * @param {Model} model The model to operate on.
 * @param {Inheritance or String} inhOrId The inheritance.
 * @param {Map} specialisedEventOptions Information about what to do with the specialised events
 * specialised along the inheritance. See CDMC_Inheritance.
 * @param {Boolean} checkSupertype check if the supertype needs to be made non-abstract?
 */
export function Combined_DeleteInheritance(
  model: IModel,
  inhOrId: OrId<IInheritance>,
  specialisedEventOptions: IdOptionMap | null | undefined,
  checkSupertype = true
) {
  const inheritance = ensureInheritance(model.metamodel, inhOrId)!;
  removeEDGInheritanceLink(model.uimodel, inheritance);
  return CDMC_Inheritance(model, inheritance, specialisedEventOptions, checkSupertype);
}

/**
 * Delete an event type and its OET row.
 *
 * @param {Model} model The model to operate on.
 * @param {EventType or String} eventOrId the event type.
 */
export function Combined_DeleteEvent(model: IModel, eventOrId: OrId<IEventType>) {
  const event = ensureEvent(model.metamodel, eventOrId)!;
  removeOETRow(model.uimodel, event);
  return CDMC_Event(model, event.id);
}

/**
 * Delete an FSM and its UI counterpart
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId the object type.
 * @param {FSM or String} fsmOrId the fsm.
 */
export function Combined_DeleteFSM(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  fsmOrId: OrId<IFSM>
) {
  const object = ensureObject(model.metamodel, objectOrId)!;
  const fsm = ensureFSM(object, fsmOrId)!;
  removeFSMView(model.uimodel, fsm);
  return CDMC_FSM(model, object, fsm);
}

/**
 * Delete an FSM state and the corresponding UI FSM node
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId the object type.
 * @param {FSM or String} fsmOrId the fsm.
 * @param {State or String} stateOrId the state.
 */
export function Combined_DeleteState(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  fsmOrId: OrId<IFSM>,
  stateOrId: OrId<IState>
) {
  const object = ensureObject(model.metamodel, objectOrId)!;
  const fsm = ensureFSM(object, fsmOrId)!;
  const state = ensureState(fsm, stateOrId)!;
  removeFSMNode(model.uimodel, fsm, state);
  return CDMC_State(model, object, fsm, state);
}

/**
 * Delete an FSM transition and the corresponding UI FSM link
 *
 * @param {Model} model The model to operate on.
 * @param {ObjectType or String} objectOrId the object type.
 * @param {FSM or String} fsmOrId the fsm.
 * @param {Transition or String} transitionOrdId the transition.
 */
export function Combined_DeleteTransition(
  model: IModel,
  objectOrId: OrId<IObjectType>,
  fsmOrId: OrId<IFSM>,
  transitionOrId: OrId<ITransition>
) {
  const object = ensureObject(model.metamodel, objectOrId)!;
  const fsm = ensureFSM(object, fsmOrId)!;
  const transition = ensureTransition(fsm, transitionOrId)!;
  removeFSMLink(model.uimodel, fsm, transition);
  return CDMC_Transition(model, object, fsm, transition);
}
